import { useTelemetry } from './useTelemetry'

type AuthMethod = 'authentik' | 'manual' | 'password' | 'token'

interface LoginSuccessOptions {
  hasRedirect?: boolean
}

interface RedirectOptions {
  frontendRoute?: string
  routeName?: string
}

const sanitizeReason = (value: string): string => {
  return (
    value
      .trim()
      .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
      .replace(/[^a-zA-Z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .toLowerCase() || 'unknown'
  )
}

export const getAuthFailureReason = (error: unknown, fallback = 'unknown'): string => {
  const maybeError = error as { code?: unknown; status?: unknown }
  if (typeof maybeError?.code === 'string' && maybeError.code.trim()) {
    return sanitizeReason(maybeError.code)
  }
  if (typeof maybeError?.status === 'number' && Number.isFinite(maybeError.status)) {
    return `http_${maybeError.status}`
  }
  return sanitizeReason(fallback)
}

export const trackAuthLoginView = (method: AuthMethod) => {
  useTelemetry().track('auth_login_view', { method })
}

export const trackAuthLoginStart = (method: AuthMethod) => {
  useTelemetry().track('auth_login_start', { method })
}

export const trackAuthLoginSuccess = (method: AuthMethod, options: LoginSuccessOptions = {}) => {
  useTelemetry().track('login', {
    has_redirect: Boolean(options.hasRedirect),
    method,
  })
}

export const trackAuthLoginFailure = (method: AuthMethod, error: unknown, stage: string) => {
  useTelemetry().track('auth_login_failed', {
    method,
    reason: getAuthFailureReason(error, stage),
    stage,
  })
}

export const trackAuthLogout = () => {
  useTelemetry().track('auth_logout', { method: 'manual' })
}

export const trackAuthSessionRestored = () => {
  useTelemetry().track('auth_session_restored', { method: 'token' })
}

export const trackAuthSessionFailure = (reason: string) => {
  useTelemetry().track('auth_session_failure', {
    reason: sanitizeReason(reason),
  })
}

export const trackAuthRedirectToLogin = (reason: string, options: RedirectOptions = {}) => {
  const eventParams: Record<string, string> = {
    reason: sanitizeReason(reason),
  }

  if (options.frontendRoute) {
    eventParams.frontend_route = options.frontendRoute
  }
  if (options.routeName) {
    eventParams.route_name = options.routeName
  }

  useTelemetry().track('auth_redirect_to_login', eventParams)
}
